import { useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getUserRoles } from '../../../utils/userRoles';
import { fetchSuperAdminStats } from '../services/superAdminService';
import SuperAdminOnlineUsersCard from '../components/SuperAdminOnlineUsersCard';
import PageHeader from '../components/PageHeader';
import StatusChip from '../components/StatusChip';
import FeedbackState, { LoadingTable } from '../components/FeedbackState';
import { fetchLaunchDashboard } from '../../../services/launchCommerceService';
import { fetchNotificationAnalytics, fetchNotificationDashboard } from '../../../services/notificationAdminService';
import { safeAdminError } from '../components/adminPresentation';

const defaultStats = {
  users: { total: 0, active: 0, archived: 0 },
  organizations: { total: 0, active: 0, archived: 0 },
  clubs: { total: 0, active: 0, archived: 0 },
  communities: { total: 0, active: 0, archived: 0 },
  tournaments: { total: 0, active: 0, archived: 0 },
};

const Kpi = ({ label, value, context, to }) => <article className="sa-kpi"><span>{label}</span><strong>{value}</strong><small>{context}</small>{to ? <NavLink to={to}>Review</NavLink> : null}</article>;
const countState = (rows = [], state) => Number(rows.find((row) => row._id === state)?.count || 0);

const SuperAdminDashboard = () => {
  const { user, token, isLoadingAuth } = useAuth();
  const userRoles = getUserRoles(user);
  const isSuperAdmin = userRoles.has('super_admin') || user?.isSuperAdmin;
  const [stats, setStats] = useState(defaultStats);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState('');
  const [launch, setLaunch] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [notificationAnalytics, setNotificationAnalytics] = useState(null);

  useEffect(() => {
    if (!token || !isSuperAdmin) {
      setIsLoadingStats(false);
      return;
    }

    let isMounted = true;

    const loadStats = async () => {
      setIsLoadingStats(true);
      setError('');
      try {
        const [statsResult, launchResult, notificationResult, analyticsResult] = await Promise.allSettled([
          fetchSuperAdminStats(token), fetchLaunchDashboard(token), fetchNotificationDashboard(token), fetchNotificationAnalytics(token),
        ]);
        if (!isMounted) return;
        if (statsResult.status === 'rejected') throw statsResult.reason;
        setStats({ ...defaultStats, ...(statsResult.value?.stats || {}) });
        setLaunch(launchResult.status === 'fulfilled' ? launchResult.value : null);
        setNotifications(notificationResult.status === 'fulfilled' ? notificationResult.value : null);
        setNotificationAnalytics(analyticsResult.status === 'fulfilled' ? analyticsResult.value : null);
      } catch (loadError) {
        if (!isMounted) return;
        setError(safeAdminError(loadError, 'Unable to load the operational dashboard.'));
      } finally {
        if (isMounted) {
          setIsLoadingStats(false);
        }
      }
    };

    loadStats();

    return () => {
      isMounted = false;
    };
  }, [isSuperAdmin, token]);

  const cards = useMemo(
    () => [
      { label: 'Active users', value: stats.users.active, context: `${stats.users.total} total`, to: '/sa/dashboard/individual' },
      { label: 'Active tournaments', value: stats.tournaments.active, context: `${stats.tournaments.total} total` },
      { label: 'Active organizations', value: stats.organizations.active, context: `${stats.organizations.total} total`, to: '/sa/organizations' },
      { label: 'Active clubs', value: stats.clubs.active, context: `${stats.clubs.total} total`, to: '/sa/clubs' },
    ],
    [stats]
  );

  const failedNotifications = Number(notificationAnalytics?.totals?.failed || 0) + Number(notificationAnalytics?.totals?.dead || 0);
  const pendingNotifications = countState(notifications?.queue, 'ready') + countState(notifications?.queue, 'leased');
  const attention = [
    failedNotifications ? { label: 'Notification delivery failures', count: failedNotifications, severity: 'Failed', to: '/sa/notifications' } : null,
    pendingNotifications ? { label: 'Notification queue awaiting processing', count: pendingNotifications, severity: 'Pending', to: '/sa/notifications' } : null,
  ].filter(Boolean);

  if (isLoadingAuth) {
    return (
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <div className="text-muted">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <section className="sa-dashboard sa-workspace">
      <PageHeader eyebrow="Overview" title="Dashboard" description="Current platform operations, launch progress, and issues requiring attention." />
      {isLoadingStats ? <LoadingTable rows={4} /> : error ? <FeedbackState type="error" title="Dashboard unavailable" message={error} /> : <>
        <div className="sa-kpi-strip"><SuperAdminOnlineUsersCard />{cards.map((card) => <Kpi key={card.label} {...card} />)}</div>
        <section className="sa-attention-panel"><header><div><span>Priority</span><h2>Needs attention</h2></div><small>{attention.length ? `${attention.length} operational ${attention.length === 1 ? 'issue' : 'issues'}` : 'Current monitored signals'}</small></header>{attention.length ? <div className="sa-attention-list">{attention.map((item) => <div key={item.label}><StatusChip value={item.severity} /><span><strong>{item.label}</strong><small>{item.count} affected records</small></span><NavLink to={item.to}>Review</NavLink></div>)}</div> : <FeedbackState title="No operational issues require attention" message="No failures are present in the monitored notification signals." />}</section>
        <div className="sa-dashboard-grid"><section className="sa-panel sa-dashboard__launch"><header className="sa-panel__header"><div><h2>Founding 100</h2><p>Qualifying Tournament Pro purchases from Launch Commerce.</p></div><NavLink className="sa-secondary-button" to="/sa/launch-commerce">Open launch workspace</NavLink></header>{launch?.goal ? <div className="sa-launch-summary"><div className="sa-launch-progress"><div><strong>{launch.goal.convertedTournaments}</strong><span>of {launch.goal.targetTournaments} claimed</span></div><progress max={launch.goal.targetTournaments || 100} value={launch.goal.convertedTournaments || 0}>{launch.goal.convertedTournaments}</progress><small>{launch.goal.remaining} qualifying places remaining</small></div><dl><div><dt>Activated</dt><dd>{launch.foundingPlacesClaimed ?? '—'}</dd></div><div><dt>Completed</dt><dd>{launch.completedTournaments ?? '—'}</dd></div><div><dt>Qualified referrals</dt><dd>{launch.referrals?.filter((item) => ['qualified', 'rewarded'].includes(item._id)).reduce((sum, item) => sum + item.count, 0) ?? '—'}</dd></div><div><dt>Checkout conversion</dt><dd>{Number.isFinite(launch.checkoutConversion) ? `${Math.round(launch.checkoutConversion * 100)}%` : '—'}</dd></div></dl></div> : <FeedbackState title="Launch metrics unavailable" message="Open the launch workspace to inspect its current configuration." />}</section><section className="sa-panel"><header className="sa-panel__header"><div><h2>Platform health</h2><p>Authoritative readiness signals only.</p></div></header><div className="sa-health-list"><div><span>Notification transport</span><StatusChip value={notifications?.providers?.some((provider) => provider.ready || provider.status === 'healthy') ? 'Active' : notifications ? 'Degraded' : 'Inactive'} /></div><div><span>Notification queue</span><StatusChip value={pendingNotifications ? 'Pending' : 'Active'} /></div><div><span>Production payments</span><StatusChip value="Inactive" label="Expected disabled" /></div><div><span>Unmonitored services</span><StatusChip value="Inactive" label="Not monitored" /></div></div></section></div>
      </>}
    </section>
  );
};

const TabContentLayout = ({ highlights = [], actions = [], screens = [], children }) => (
  <div className="d-grid gap-3">
    {highlights.length > 0 && (
      <div>
        <p className="fw-semibold mb-2">What you can do</p>
        <ul className="mb-0 text-muted">
          {highlights.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    )}

    {actions.length > 0 && (
      <div className="d-flex flex-column flex-sm-row gap-2">
        {actions.map((action) => (
          <NavLink
            key={action.label}
            to={action.to}
            className={`btn btn-${action.variant || 'primary'}`}
          >
            {action.label}
          </NavLink>
        ))}
      </div>
    )}

    {screens.length > 0 && (
      <div className="d-grid gap-2 gap-md-3">
        <p className="fw-semibold mb-2">Screens in this flow</p>
        <div className="row g-3">
          {screens.map((screen) => (
            <div className="col-12 col-md-6 col-lg-4" key={screen.title}>
              <div className="border rounded h-100 p-3 d-grid gap-2">
                <div className="d-flex align-items-center gap-2 z-1">
                  <span className="badge bg-light text-dark border small">{screen.type || 'Screen'}</span>
                  {screen.tag && <span className="badge bg-primary-subtle text-primary small">{screen.tag}</span>}
                </div>
                <div className="d-grid gap-1 z-1">
                  <p className="fw-semibold mb-0">{screen.title}</p>
                  <p className="text-muted small mb-0">{screen.description}</p>
                </div>
                {screen.to && (
                  <NavLink className="text-decoration-none small fw-semibold mt-auto" to={screen.to}>
                    Open screen →
                  </NavLink>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {children}
  </div>
);

const TabbedContentSwitcher = ({ sections, defaultKey }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionKeys = sections?.map((section) => section.key) || [];
  const requestedKey = searchParams.get('section');
  const fallbackKey = sectionKeys.includes(defaultKey) ? defaultKey : sectionKeys[0];
  const activeKey = sectionKeys.includes(requestedKey) ? requestedKey : fallbackKey;
  const activeSection = sections?.find((section) => section.key === activeKey) || sections?.[0];

  useEffect(() => {
    if (!requestedKey && activeKey) {
      const params = new URLSearchParams(searchParams);
      params.set('section', activeKey);
      setSearchParams(params, { replace: true });
    }
  }, [activeKey, requestedKey, searchParams, setSearchParams]);

  return (
    <div className="d-grid gap-3">
      <div className="d-flex flex-wrap gap-2">
        {sections?.map((section) => (
          <NavLink
            key={section.key}
            to={`?section=${section.key}`}
            className={({ isActive }) =>
              `btn btn-sm ${activeKey === section.key || isActive ? 'btn-primary' : 'btn-outline-secondary'}`
            }
          >
            <span className="fw-semibold d-block">{section.label}</span>
            {section.description && <span className="small fw-normal text-light-emphasis d-block">{section.description}</span>}
          </NavLink>
        ))}
      </div>

      <div className="d-grid gap-3">{activeSection?.content}</div>
    </div>
  );
};

export const SuperAdminTabContent = TabContentLayout;

export const SuperAdminTab = ({
  title,
  description,
  highlights = [],
  actions = [],
  screens = [],
  children,
  sections,
}) => {
  const hasSections = Array.isArray(sections) && sections.length > 0;

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body d-grid gap-3">
        <div>
          <p className="text-muted small mb-1">{title}</p>
          <h2 className="h4 mb-2">Explore {title.toLowerCase()}</h2>
          <p className="mb-0 text-muted">{description}</p>
        </div>

        {hasSections ? (
          <TabbedContentSwitcher sections={sections} />
        ) : (
          <TabContentLayout highlights={highlights} actions={actions} screens={screens}>
            {children}
          </TabContentLayout>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
